import type { Roll } from '@shared/types'
import { cn } from '@/lib/cn'

const OUTCOME_RING = {
  crit: 'border-moss-500/60 bg-moss-900/30',
  fumble: 'border-blood-500/60 bg-blood-900/30',
  normal: 'border-ink-700/70 bg-ink-850/70',
} as const

const ADVANTAGE_LABEL = {
  advantage: 'with advantage',
  disadvantage: 'with disadvantage',
  none: '',
} as const

/** One die face. Dropped dice stay visible so advantage is legible at a glance. */
function Die({ value, sides, dropped, negative }: Roll['dice'][number]) {
  return (
    <span
      title={`d${sides}${dropped ? ' (dropped)' : ''}`}
      className={cn(
        'inline-grid size-7 place-items-center rounded-md border font-mono text-xs tabular-nums',
        dropped
          ? 'border-ink-700 text-ink-600 line-through'
          : sides === 20 && value === 20 ? 'border-moss-500 bg-moss-900/60 text-moss-500'
          : sides === 20 && value === 1 ? 'border-blood-500 bg-blood-900/60 text-blood-500'
          : 'border-ink-600 bg-ink-800 text-ink-200',
      )}
    >
      {negative && !dropped ? `−${value}` : value}
    </span>
  )
}

export function RollResultCard({ roll, showWho = true }: { roll: Roll; showWho?: boolean }) {
  const who = roll.characterName ?? roll.userName ?? 'Someone'

  return (
    <div className={cn('rounded-xl border p-3', OUTCOME_RING[roll.outcome])}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {showWho && (
            <p className="truncate text-sm text-ink-100">
              {who}
              {roll.characterName && roll.userName && (
                <span className="ml-1.5 text-xs text-ink-500">({roll.userName})</span>
              )}
            </p>
          )}
          <p className="truncate text-xs text-ink-400">
            {roll.context || 'Dice roll'}
            {roll.advantage !== 'none' && (
              <span className="ml-1 text-gold-400">{ADVANTAGE_LABEL[roll.advantage]}</span>
            )}
          </p>
        </div>

        <div className="shrink-0 text-right">
          <p
            className={cn(
              'font-mono text-2xl leading-none tabular-nums',
              roll.outcome === 'crit' ? 'text-moss-500'
              : roll.outcome === 'fumble' ? 'text-blood-500'
              : 'text-ink-100',
            )}
          >
            {roll.total}
          </p>
          {roll.outcome !== 'normal' && (
            <p className={cn('text-[10px] font-semibold uppercase tracking-wider',
              roll.outcome === 'crit' ? 'text-moss-500' : 'text-blood-500')}>
              {roll.outcome === 'crit' ? 'Natural 20' : 'Natural 1'}
            </p>
          )}
        </div>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
        {roll.dice.map((die, index) => <Die key={index} {...die} />)}
        {roll.modifier !== 0 && (
          <span className="font-mono text-xs text-ink-400">
            {roll.modifier < 0 ? '−' : '+'} {Math.abs(roll.modifier)}
          </span>
        )}
        <span className="ml-auto font-mono text-[11px] text-ink-600">{roll.formula}</span>
      </div>

      {roll.private && (
        <p className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-arcane-400">
          Hidden from players
        </p>
      )}
    </div>
  )
}
